import React, { ReactElement } from 'react';

import { Inject } from '@odinjs/odin';

import providerContext from '@philips-emr/hpms-provider-commons/lib/context/provider-context';
import { ContextualBarAPI, FetchAPI, FieldAPI, Stamp, StampIcon } from '@philips-emr/tws-components';
import { Color } from '@philips-emr/tws-constants';
import Controller, { OnAfterFetch, OnDynamicContentRender, OnMount, OnSelectItem } from '@philips-emr/tws-core/lib/client';
import { FetchEvent, MountEvent, RenderEvent, SelectItemEvent } from '@philips-emr/tws-core/lib/component/types';
import { MessageService } from '@philips-emr/tws-core/lib/services';
import { isEmptyArrayOrObject } from '@philips-emr/tws-utils';

import { TaskDetailsAliases as Aliases, FragmentNames } from '../../codegen';
import { Route } from '../../route';
import BaseController from '../BaseController';

import { Task, TaskStatus, TaskType } from './tasks-controller';

import './task.css';

enum IndicatorStatus {
  PENDING = '1',
  SOLVED = '2',
  CANCELED = '3',
  AWAITING_VALIDATION = '4',
}

@Controller({ fragment: FragmentNames.TASK_DETAILS })
class TaskDetailsController extends BaseController {

  @Inject(Aliases.CONTEXTUAL_BAR_ITENS)
  private contextualBarItens: ContextualBarAPI;
  @Inject(Aliases.FIELD_CONCLUSION_DATE)
  private fieldConclusionDate: FieldAPI;

  @OnAfterFetch(Aliases.FETCH_TASK_DETAILS)
  onMountConclusionDate(event: FetchEvent): void {
    if (event.data) {
      const { writeOffDate } = event.data as unknown as Task;
      this.fieldConclusionDate.setState({ hideLabel: isEmptyArrayOrObject(writeOffDate) });
    }
  }

  @OnDynamicContentRender(Aliases.FIELD_STATUS_INDICATOR)
  OnDynamicContentRenderStatusIndicator(event: RenderEvent): void {
    const { data, render } = event;
    const { ieStatusIndicator, dsStatus } = data as Task;
    const content = (): ReactElement => (getStampStatusIndicator(ieStatusIndicator, dsStatus));
    render(content);
  }

  @OnMount([Aliases.FETCH_TASK_DETAILS, Aliases.FETCH_TASK_ITENS])
  onMountFetchs(event: MountEvent): void {
    const api = event.target as FetchAPI;
    const nrSeqProvider = providerContext.getSelectedProvider().nrSeqProvider;
    const { nrSequenceTask } = this.historyService.getCurrentRouteParams();
    api.setParameters({ nrSeqProvider, nrSequenceTask });
    api.forceFetch();
  }

  @OnAfterFetch(Aliases.FETCH_TASK_ITENS)
  onAfterfetchItens(event: FetchEvent): void {
    if (event.data) {
      const data = event.data as Task[];
      const { taskType } = data[0];
      let title = '';
      switch (taskType) {
        case TaskType.INDICATORS:
          title = MessageService.translate('provider.indicators');
          break;
        case TaskType.DOCUMENTS:
          title = MessageService.translate('provider.documents');
          break;
        case TaskType.ACTION_PLAN:
          title = MessageService.translate('provider.action-plan');
          break;
        case TaskType.MEETING_MINUTES:
          title = MessageService.translate('provider.meeting-minute-consultation');
          break;
      }
      this.contextualBarItens.setTitle(title);
    }
  }

  @OnSelectItem(Aliases.DECK_INDICATORS)
  onClickIndicators(event: SelectItemEvent<Task>): void {
    const { taskType, nrSeqDocumentType, nrSeqProviderIndicator, nrSeqActionPlan, nrSeqProviderMeetingMinute } = event.item;

    switch (taskType) {
      case TaskType.INDICATORS:
        this.historyService.push(Route.INDICATOR_DETAILS, { nrSeqProviderIndicator });
        break;
      case TaskType.DOCUMENTS:
        this.historyService.push(Route.DOCUMENTS_DETAILS, { nrSeqDocumentType });
        break;
      case TaskType.ACTION_PLAN:
        this.historyService.push(Route.ACTION_PLAN_DETAILS, { nrSeqActionPlan });
        break;
      case TaskType.MEETING_MINUTES:
        this.historyService.push(Route.MEETING_MINUTE_DETAILS, { nrSeqProviderMeetingMinute });
        break;
    }
  }

  @OnMount([Aliases.FIELD_INDICATOR_TYPE, Aliases.FIELD_COMPETENCE])
  onMountFieldsIndicator(event: MountEvent): void {
    const api = event.target as FieldAPI;
    this.validateFieldVisibility(api, TaskType.INDICATORS);
  }

  @OnMount(Aliases.FIELD_DOC_TYPE)
  onMountFieldsDocument(event: MountEvent): void {
    const api = event.target as FieldAPI;
    this.validateFieldVisibility(api, TaskType.DOCUMENTS);
  }

  @OnMount(Aliases.FIELD_ACTION)
  onMountFieldsAction(event: MountEvent): void {
    const api = event.target as FieldAPI;
    this.validateFieldVisibility(api, TaskType.ACTION_PLAN);
  }

  @OnMount(Aliases.FIELD_MEETING_MINUTES)
  onMountFieldsMeetingMinute(event: MountEvent): void {
    const api = event.target as FieldAPI;
    this.validateFieldVisibility(api, TaskType.MEETING_MINUTES);
  }

  private validateFieldVisibility(api: FieldAPI, type: TaskType): void {
    const data = api.getData();
    if (data) {
      const { taskType } = data as unknown as Task;
      api.setVisible(taskType == type);
    }
  }

}

function getStampStatusTask(ieStatus: TaskStatus, label: string): ReactElement {
  let stampProps = null;

  const icon = {
    [TaskStatus.PENDING]: { color: Color.SAFETY_ORANGE, icon: StampIcon.WARNING, label },
    [TaskStatus.AWAINTING_VALIDATION]: { color: Color.SAFETY_BLUE, icon: StampIcon.CLOCK, label },
    [TaskStatus.CLOSED]: { color: Color.SAFETY_GREEN, icon: StampIcon.CHECK, label },
    [TaskStatus.CANCELED]: { color: Color.SAFETY_NEUTRAL, icon: StampIcon.CANCELED, label },
  };

  stampProps = icon[ieStatus];
  return <Stamp {...stampProps} />;
}

function getStampStatusIndicator(ieStatus: IndicatorStatus, label: string): ReactElement {
  let stampProps = null;

  const icon = {
    [IndicatorStatus.AWAITING_VALIDATION]: { color: Color.SAFETY_BLUE, icon: StampIcon.CLOCK, label },
    [IndicatorStatus.SOLVED]: { color: Color.SAFETY_GREEN, icon: StampIcon.CHECK, label },
    [IndicatorStatus.CANCELED]: { color: Color.SAFETY_NEUTRAL, icon: StampIcon.CANCELED, label },
    [IndicatorStatus.PENDING]: { color: Color.SAFETY_ORANGE, icon: StampIcon.WARNING, label },

  };

  stampProps = icon[ieStatus];
  return <Stamp {...stampProps} />;
}

export {
  TaskDetailsController,
  IndicatorStatus,
  getStampStatusTask,
};
